/* eslint-disable */
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import React, {useState} from 'react';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {logoutUser} from '../../slice/loginSlice';
import {LogoutConfirm} from '../../components/LogoutConfirm';
import PompiLogo from '../../assets/PompiImage.svg';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import AppIcon from '../../components/onboarding/AppIcon';
import {card, colors, radii, spacing, typography} from '../../config/theme';
import {formatDrivers, minutesAgo} from '../../config/onboardingData';
import {useOnboarding} from './OnboardingContext';

const Welcome = ({navigation}) => {
  const {t} = useTranslation();
  // Numbers come from GET /v1/onboarding/config (see OnboardingContext).
  // While it loads, or if it fails, the fallback data is used.
  const {config, loading: isLoading, currency: c} = useOnboarding();
  const w = config.welcome;
  const stats = {
    avgCityPrice: w.otherStationsPrice,
    pompiBestPrice: w.pompiBestPrice,
    // Falls back to 2 min so the "Verified" line is always visible.
    verifiedMinutesAgo: minutesAgo(w.verifiedAt) ?? 2,
    rating: String(w.stats.rating),
    driversCount: formatDrivers(w.stats.drivers),
  };
  const savePerFill = w.savingsPerFillUp;

  const handleStart = () => navigation.navigate('FuelType');

  // A logged-in user who hasn't finished onboarding lands here and can't
  // reach Profile, so this is their only way to switch accounts.
  const dispatch = useDispatch();
  const [logoutVisible, setLogoutVisible] = useState(false);
  const handleLogout = async () => {
    await dispatch(logoutUser());
    // The login stack stays mounted on AfterSignUp, so send it back to Login.
    navigation.getParent()?.reset({index: 0, routes: [{name: 'Login'}]});
  };

  return (
    <SafeAreaView style={styles.safe}>
      <LogoutConfirm
        modalVisible={logoutVisible}
        setModalVisible={setLogoutVisible}
        onConfirm={handleLogout}
      />
      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}>
        <View style={styles.top}>
          <PompiLogo width={160} height={145} />

          <View style={styles.topBadge}>
            <AppIcon name="bolt" size={13} color={colors.onPrimary} />
            <Text style={styles.topBadgeText}>
              {t('root.AfterSignUp.badge', 'SAVE MONEY ON FUEL')}
            </Text>
          </View>

          <Text style={styles.title}>
            {t('root.AfterSignUp.headline', 'STOP PAYING TOO MUCH.')}
          </Text>
          <Text style={styles.subtitle}>
            {t(
              'root.AfterSignUp.subheadline',
              'Find the cheapest pump in seconds.',
            )}
          </Text>

          {isLoading ? (
            <ActivityIndicator
              size="large"
              color={colors.primary}
              style={styles.loader}
            />
          ) : (
            <View style={styles.card}>
              {/* Other stations */}
              <View style={[styles.row, styles.badRow]}>
                <View style={styles.rowLeft}>
                  <Text style={styles.badLabel}>
                    ✕  {t('root.AfterSignUp.otherStations', 'OTHER STATIONS')}
                  </Text>
                  <Text style={styles.badSub}>
                    {t('root.AfterSignUp.avgPump', 'Average city pump')}
                  </Text>
                </View>
                <View style={styles.priceCol}>
                  <Text style={styles.badPrice}>
                    {c}
                    {stats.avgCityPrice.toFixed(2)}
                  </Text>
                  <Text style={styles.badPerL}>/ {w.unit}</Text>
                </View>
              </View>

              {/* Pompi best price */}
              <View style={[styles.row, styles.goodRow]}>
                <View style={styles.rowLeft}>
                  <Text style={styles.goodLabel}>
                    ✓  {t('root.AfterSignUp.bestPrice', 'POMPI BEST PRICE')}
                  </Text>
                  <Text style={styles.goodSub}>
                    {t(
                      'root.AfterSignUp.verified',
                      'Verified {{min}} min ago',
                      {min: stats.verifiedMinutesAgo},
                    )}
                  </Text>
                </View>
                <View style={styles.priceCol}>
                  <Text style={styles.goodPrice}>
                    {c}
                    {stats.pompiBestPrice.toFixed(2)}
                  </Text>
                  <Text style={styles.goodPerL}>/ {w.unit}</Text>
                </View>
              </View>

              <View style={styles.saveBadge}>
                <Text style={styles.saveBadgeText}>
                  {t('root.AfterSignUp.save', 'SAVE {{amount}} PER FILL-UP', {
                    amount: `${c}${savePerFill}`,
                  })}
                </Text>
              </View>
            </View>
          )}

          <View style={styles.chips}>
            <Chip
              icon="star"
              text={`${stats.rating} ${t('root.AfterSignUp.rating', 'Rating')}`}
            />
            <Chip
              icon="car"
              text={`${stats.driversCount} ${t('root.AfterSignUp.drivers', 'Drivers')}`}
            />
            <Chip
              icon="check"
              text={t('root.AfterSignUp.free', 'Free to try')}
            />
          </View>
        </View>

        <View style={styles.bottom}>
          {/* Pill-shaped button, local to this screen only */}
          <Pressable
            onPress={handleStart}
            accessibilityRole="button"
            style={({pressed}) => [
              styles.startButton,
              pressed && styles.startButtonPressed,
            ]}>
            <Text style={styles.startButtonText}>
              {t('root.AfterSignUp.start')}
            </Text>
            <ArrowRight width={16} height={16} color={colors.onPrimary} />
          </Pressable>
          <Text style={styles.footnote}>
            {t('root.AfterSignUp.footnote', 'Takes 30 seconds • No credit card')}
          </Text>
          <Pressable
            onPress={() => setLogoutVisible(true)}
            accessibilityRole="button"
            hitSlop={8}
            style={styles.switchAccount}>
            <Text style={styles.switchAccountText}>
              {t('root.AfterSignUp.notYou', 'Not you?')}{' '}
              <Text style={styles.switchAccountLink}>
                {t('root.AfterSignUp.switchAccount', 'Use another account')}
              </Text>
            </Text>
          </Pressable>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const Chip = ({icon, text}) => (
  <View style={styles.chip}>
    <AppIcon name={icon} size={14} />
    <Text style={styles.chipText}>{text}</Text>
  </View>
);

// NOTE: no fontWeight anywhere. Weight comes from the PlusJakartaSans font
// files via typography tokens (see config/theme.js).
const styles = StyleSheet.create({
  safe: {flex: 1, backgroundColor: colors.background},
  scroll: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  top: {alignItems: 'center', gap: spacing.md},
  loader: {marginVertical: spacing.xl},

  // The logo already contains the "pompi" wordmark, so the badge sits
  // below it instead of overlapping.
  topBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.primary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
  },
  topBadgeText: {...typography.labelSm, color: colors.onPrimary},

  title: {
    ...typography.displayMobile,
    color: colors.primary,
    textAlign: 'center',
  },
  subtitle: {...typography.bodyLg, color: colors.textMuted, textAlign: 'center'},

  // Comparison card
  card: {...card, width: '100%', padding: spacing.sm + 2},
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md + 4,
    borderRadius: radii.sm,
  },
  rowLeft: {flexShrink: 1, paddingRight: spacing.sm},
  priceCol: {alignItems: 'flex-end'},

  badRow: {
    backgroundColor: colors.errorTint,
    borderWidth: 1,
    borderColor: colors.errorContainer,
  },
  badLabel: {...typography.labelMd, color: colors.error},
  badSub: {...typography.bodySm, color: colors.textMuted, marginTop: spacing.xs},
  badPrice: {
    ...typography.headlineLg,
    color: colors.error,
    textDecorationLine: 'line-through',
  },
  badPerL: {...typography.labelMd, color: colors.error},

  goodRow: {
    backgroundColor: colors.primary,
    marginTop: spacing.sm + 2,
    paddingBottom: spacing.lg + 6, // room for the overlapping save badge
  },
  goodLabel: {...typography.labelMd, color: colors.tertiary},
  goodSub: {
    ...typography.bodySm,
    color: colors.onPrimary, // brighter than onPrimaryMuted so it stays visible
    marginTop: spacing.xs,
  },
  goodPrice: {...typography.displayLg, color: colors.tertiary},
  goodPerL: {...typography.labelMd, color: colors.tertiary},

  saveBadge: {
    alignSelf: 'center',
    marginTop: -spacing.md - 2,
    backgroundColor: colors.tertiary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md + 2,
    paddingVertical: spacing.xs + 2,
  },
  saveBadgeText: {...typography.labelSm, color: colors.primary},

  message: {
    ...typography.bodyMd,
    color: colors.primary,
    textAlign: 'center',
  },

  // Stat chips
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.surfaceMuted,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 3,
  },
  chipText: {...typography.labelMd, color: colors.text},

  // CTA
  bottom: {marginTop: spacing.xl},

  // Pill-shaped "Start saving now" button (this screen only)
  startButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 56,
    borderRadius: 999, // fully rounded ends
    backgroundColor: colors.primary,
    overflow: 'hidden',
  },
  startButtonPressed: {opacity: 0.85},
  startButtonText: {
    ...typography.labelMd,
    fontSize: 16,
    color: colors.onPrimary,
  },

  footnote: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  switchAccount: {alignSelf: 'center', marginTop: spacing.md},
  switchAccountText: {...typography.bodySm, color: colors.textMuted},
  switchAccountLink: {...typography.labelMd, color: colors.primary},
});

export default Welcome;